import { ModuleRegistry } from "ag-grid-community";
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MultiFilterModule,
  PivotModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  SetFilterModule,
  SideBarModule,
  TreeDataModule,
} from "ag-grid-enterprise";
import { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";

import { AgGridReact } from "ag-grid-react";
import { agApiSizeColumnsToFit } from "../../../../_src_shared/utils/agGrid/api/agApiSizeColumnsToFit";
import { columnTypesBoolean } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesBoolean";
import { columnTypesDate } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { useDynamicColumnDefs } from "../../../../_src_shared/utils/agGrid/hooks/useDynamicColumnDefs";
import AgGridTableWrapper from "../../../agGrid/AgGridTableWrapper";
import CellRendererUserByEmail from "../../../agGrid/cellRenderer/CellRendererUserByEmail";
import { isBoolean, isFunction, isNil, size } from "lodash";
import { agApiAutoSizeColumns } from "../../../../_src_shared/utils/agGrid/api/agApiAutoSizeColumns";
import Flx from "../../../layout/Flx";
import CommentsRenderer from "../../../renderers/CommentsRenderer";
import { useSelector } from "react-redux";
import { Checkbox } from "@mui/material";
import { blue } from "@mui/material/colors";
import CellRendererCommentsThreadPreview from "../../../agGrid/cellRenderer/CellRendererCommentsThreadPreview";

ModuleRegistry.registerModules([
  SideBarModule,
  RowGroupingPanelModule,
  MultiFilterModule,
  RowGroupingModule,
  PivotModule,
  TreeDataModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  SetFilterModule,
]);

const LoanExceptionsTable = forwardRef(
  (
    {
      rowData,
      onRowClicked,
      simpleTable,
      selectedId,
      masterDetail,
      quickFilterText,
    },
    ref,
  ) => {
    const exception = useSelector((state) => state?.sidebar?.state);

    const columnDefsFull = useMemo(() => {
      return [
        {
          headerName: "Status",
          field: "exceptionStatus",
          maxWidth: 100,
          flex: 0,
        },
        {
          headerName: "Exception Type",
          field: "taskListItemId",
          valueFormatter: (params) => {
            if (params?.data?.taskListItemId) return "Soft Exception";
            return "Hard Exception";
          },
          minWidth: 100,
          flex: 0,
        },
        {
          headerName: "Description",
          field: "exceptionDescription",
          // suppressAutoSize: true,
          // suppressSizeToFit: true,
          minWidth: 250,
        },
        {
          headerName: "Compensating Factors",
          field: "compensatingFactors",
          suppressAutoSize: true,
          suppressSizeToFit: true,
          wrapText: true,
          autoHeight: true,
          minWidth: 250,
          // cellStyle: { paddingTop: "16px", paddingBottom: "16px" },
        },
        {
          headerName: "Due Date",
          field: "dueDateTime",
          type: "date",
          maxWidth: 100,
        },
        {
          headerName: "Assigned To",
          field: "assignedTo",
          cellRenderer: CellRendererUserByEmail,
          width: 150,
        },
        {
          headerName: "Exception Category",
          field: "exceptionCategory",
        },
        {
          headerName: "Exception Date",
          field: "exceptionDate",
          type: "date",
        },

        {
          headerName: "Resolution Date",
          field: "resolutionDateTime",
          type: "date",
          width: 150,
        },
        {
          headerName: "Risk Bucket",
          field: "riskBucket",
          width: 150,
          minWidth: 120,
        },
        {
          headerName: "Send Email",
          field: "sendEmail",
          width: 150,
          valueGetter: (p) => {
            if (
              isNil(p?.data?.sendEmail) ||
              !p?.data?.sendEmail ||
              !isBoolean(p?.data?.sendEmail)
            ) {
              return false;
            } else {
              return true;
            }
          },
          cellRenderer: (p) => {
            return (
              <Flx sx={{ height: "100%" }}>
                <Checkbox sx={{ p: 0 }} checked={p.value} disabled />
              </Flx>
            );
          },
        },

        {
          headerName: "Created By",
          field: "createdBy",
          cellRenderer: CellRendererUserByEmail,
        },
        {
          headerName: "Task List Item ID",
          field: "taskListItemId",
          hide: true,
        },
        {
          headerName: "Update Date",
          field: "updateDateTime",
          type: "date",
        },
        {
          pinned: "right",
          field: "_id",
          valueGetter: (p) => {
            const comments =
              p.data?.comments || p.data?.conditionComments || [];
            return comments?.length > 0 ? comments : null;
          },
          cellClass: "ag-cell-vertical-center",
          headerName: "Comments",
          cellStyle: { cursor: "pointer" },
          width: 100,
          flex: 0,
          minWidth: 100,
          cellRenderer: (p) => (
            <CellRendererCommentsThreadPreview comments={p.data?.comments} />
          ),

          // cellRenderer: (p) => {
          //   return (
          //     <Flx center fw sx={{ height: "100%" }}>
          //       <CommentsRenderer onlyCount comments={p.data?.comments} />
          //     </Flx>
          //   );
          // },
        },
      ];
    }, []);

    const dynamicColumnDefs = useDynamicColumnDefs(rowData, columnDefsFull);

    const maxColDefSize = useMemo(() => {
      return 10;
    }, []);

    const columnDefs = useMemo(() => {
      if (size(dynamicColumnDefs) < maxColDefSize) {
        return columnDefsFull;
      }
      return dynamicColumnDefs;
    }, [dynamicColumnDefs, columnDefsFull, maxColDefSize]);

    const handleOnRowClicked = useCallback(
      (params) => {
        if (isFunction(onRowClicked)) onRowClicked(params?.data);
      },
      [onRowClicked],
    );

    const defaultColDef = useMemo(
      () => ({
        filter: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        resizeable: true,
        flex: 1,
        minWidth: 150,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          paddingTop: "16px",
          paddingBottom: "16px",
          lineHeight: "18px",
        },
        valueGetter: (params) => {
          const def = params?.colDef?.field;
          const data = params?.data || {};
          const value = data[def];

          if (value == null || value === "") return "—";
          return value;
        },
      }),
      [],
    );

    const columnTypes = useMemo(
      () => ({
        ...columnTypesDate(),
        ...columnTypesBoolean(),
      }),
      [],
    );

    const getRowStyle = useCallback(
      (params) => {
        if (exception?._id === params?.data?._id) {
          return {
            backgroundColor: "#e3f2fd63",
            fontWeight: 600,
            borderLeft: `3px solid ${blue[500]}`,
          };
        }
      },
      [exception?._id],
    );

    return (
      <AgGridTableWrapper
        suppressMinHeightWhenPopulated={rowData}
        // height={height}
        // simpleTable
        compactTable
        sx={{ minWidth: "340px" }}
      >
        <AgGridReact
          ref={ref}
          rowData={rowData}
          // columnDefs={dynamicColumnDefs}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowStyle={getRowStyle}
          quickFilterText={quickFilterText}
          columnTypes={columnTypes}
          domLayout={"autoHeight"}
          onRowClicked={handleOnRowClicked}
          //   detailCellRendererParams={{ refreshStrategy: "rows" }}
          //   detailRowAutoHeight={true}
        />
      </AgGridTableWrapper>
    );
  },
);

const CellRenderer = ({ children }) => {
  return <>{children}</>;
};

export default LoanExceptionsTable;
